import { createContext } from 'preact'
import type { ComponentType } from 'preact'
import { useContext } from 'preact/hooks'
import type { LocalizedText, ProductView, VendorView, WalletView } from '#contract'
import { assert } from '#shared'
import type { OsState } from '../../os-state.ts'
import type { Basket } from './basket.ts'
import type { ShopNav } from './nav.ts'
import type { BasketSummary } from './summary.ts'

/**
 * What makes a shop app of the kit: its shop, its look and how its home lists the sections.
 * The next shop app is a skin: this, its tokens and a class mapping them.
 */
export interface ShopSkin {
  /** The OS app's id. */
  readonly app: string
  /** The vendor it orders from (`view.shops.vendors`). */
  readonly vendor: string
  /** The brand bar's content on the home page, left of the balance; `vendor` once known. */
  readonly brand: ComponentType<{ readonly vendor: VendorView | null }>
  /** A big line under the home page's bar ("Hungry? We deliver."). */
  readonly headline?: LocalizedText
  /**
   * The home page: `cards` (a card per section: restaurants) or `aisles` (section tiles
   * over every product; chips filtering a grid on the desktop).
   */
  readonly home: 'cards' | 'aisles'
  /** The home page's heading over the sections ("Restaurants", "Aisles"). */
  readonly sectionsTitle: LocalizedText
  /** Maps the kit's `--shop-*` colours to the app's `--app-<id>-*` tokens. */
  readonly class: string | undefined
}

/** One open shop app: what its pages read. */
export interface Shop {
  readonly os: OsState
  readonly skin: ShopSkin
  readonly vendor: VendorView
  readonly wallet: WalletView
  /** Every product the vendor sells, by item. */
  readonly products: ReadonlyMap<string, ProductView>
  readonly basket: Basket
  /** The basket priced (it re-renders the app as it changes). */
  readonly summary: BasketSummary
  readonly nav: ShopNav
  /** Cents in the wallet's currency and the UI's locale. */
  readonly money: (cents: number) => string
}

export const ShopContext = createContext<Shop | null>(null)

export function useShop(): Shop {
  const shop = useContext(ShopContext)
  assert(shop, 'useShop() needs a <ShopApp> above it')
  return shop
}
